import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { MeetingPrepMessagePart, TodoChecklist as TodoChecklistSchema } from "@acto/genui-components-schema";
import type { Todo } from "@acto/taskpane/core/domain";
import { useTodosByIds } from "@acto/taskpane/core/data-access/todos/useTodosByIds";

/** How long after the last commit the all-done confirmation replaces the story. */
export const DONE_APPEAR_MS = 1500;
/** How long the confirmation stays up before the story closes itself. */
export const DONE_CLOSE_MS = 3200;

export interface MeetingPrepTodosState {
  todosById: ReadonlyMap<string, Todo>;
  /**
   * Ids that were OPEN when the id set first resolved. Frozen for the session, so completing a
   * to-do never re-runs the story's render decision underneath the confirmation sequence that
   * owns the close.
   */
  openIdsAtLoad: ReadonlySet<string>;
  isPending: boolean;
  /** Checklists that currently show the all-done confirmation capsule. */
  confirmingCardKeys: ReadonlySet<string>;
  /** Checklists dismissed in this session (all own items done, confirmation shown, closed). */
  dismissedCardKeys: ReadonlySet<string>;
  /**
   * Checklists that still list an open to-do assigned to someone else. Their delegated section
   * outlives the viewer's own rows, so the confirmation and the dismissal apply to the checklist
   * card alone; without one they apply to the whole story.
   */
  delegatedOpenCardKeys: ReadonlySet<string>;
  /**
   * The checklist reports whether every row of its own is committed, and whether it still holds an
   * open delegated to-do. All own rows committed starts the all-done sequence (confirmation after
   * DONE_APPEAR_MS, close after DONE_CLOSE_MS); false before the confirmation appears cancels it.
   * Once the confirmation is up there is no way back.
   */
  reportChecklistState: (cardKey: string, allOwnCommitted: boolean, hasOpenDelegated: boolean) => void;
}

const EMPTY_STATE: MeetingPrepTodosState = {
  todosById: new Map(),
  openIdsAtLoad: new Set(),
  isPending: false,
  confirmingCardKeys: new Set(),
  dismissedCardKeys: new Set(),
  delegatedOpenCardKeys: new Set(),
  reportChecklistState: () => undefined,
};

const MeetingPrepTodosContext = createContext<MeetingPrepTodosState>(EMPTY_STATE);
export const MeetingPrepTodosProvider = MeetingPrepTodosContext.Provider;

export function useMeetingPrepTodosContext(): MeetingPrepTodosState {
  return useContext(MeetingPrepTodosContext);
}

/** All to-do ids referenced by the prep's todoChecklist cards, deduplicated, in payload order. */
export function todoChecklistIds(parts: readonly MeetingPrepMessagePart[]): string[] {
  const ids = new Set<string>();
  for (const part of parts) {
    if (part.type !== "storyCard") continue;
    for (const point of part.talkingPoints) {
      if (point.type === "todoChecklist") point.todoIds.forEach((id) => ids.add(id));
    }
  }
  return [...ids];
}

/** A story whose ONLY talking point is the checklist; one mixed into other cards never counts. */
export function isTodoStory(
  part: MeetingPrepMessagePart,
): part is Extract<MeetingPrepMessagePart, { type: "storyCard" }> {
  return part.type === "storyCard" && part.talkingPoints.length === 1 && part.talkingPoints[0].type === "todoChecklist";
}

export function isTodoChecklistRenderable(point: TodoChecklistSchema, state: MeetingPrepTodosState): boolean {
  if (point.cardKey && state.dismissedCardKeys.has(point.cardKey) && !state.delegatedOpenCardKeys.has(point.cardKey))
    return false;
  return point.todoIds.some((id) => state.openIdsAtLoad.has(id));
}

/** What the checklist card itself shows: its rows, the all-done confirmation, or nothing. */
export function todoChecklistCardPhase(
  point: TodoChecklistSchema,
  state: MeetingPrepTodosState,
): "rows" | "confirmation" | "closed" {
  const cardKey = point.cardKey;
  if (!cardKey || !state.delegatedOpenCardKeys.has(cardKey)) return "rows";
  if (state.dismissedCardKeys.has(cardKey)) return "closed";
  return state.confirmingCardKeys.has(cardKey) ? "confirmation" : "rows";
}

/**
 * Whether a part renders anything. A story whose only card is a to-do checklist disappears when
 * none of its listed to-dos were open at load (the auto-dismiss); while the ids are still
 * loading it counts as renderable.
 */
export function storyHasRenderableContent(part: MeetingPrepMessagePart, state: MeetingPrepTodosState): boolean {
  if (part.type !== "storyCard") return true;
  return part.talkingPoints.some((point) =>
    point.type === "todoChecklist" ? state.isPending || isTodoChecklistRenderable(point, state) : true,
  );
}

/** Loads every to-do referenced by the prep once and freezes the open set per id list. */
export function useMeetingPrepTodos(parts: readonly MeetingPrepMessagePart[]): MeetingPrepTodosState {
  const ids = useMemo(() => todoChecklistIds(parts), [parts]);
  const idsKey = ids.join("|");
  const { data, isPending, error } = useTodosByIds(ids);

  const [frozen, setFrozen] = useState<{ key: string; openIds: ReadonlySet<string> } | null>(null);
  useEffect(() => {
    if (!data || frozen?.key === idsKey) return;
    setFrozen({ key: idsKey, openIds: new Set(data.filter((todo) => todo.isOpen()).map((todo) => todo.id)) });
  }, [data, idsKey, frozen]);

  const [confirmingCardKeys, setConfirmingCardKeys] = useState<ReadonlySet<string>>(new Set());
  const [dismissedCardKeys, setDismissedCardKeys] = useState<ReadonlySet<string>>(new Set());
  const [delegatedOpenCardKeys, setDelegatedOpenCardKeys] = useState<ReadonlySet<string>>(new Set());
  const appearTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const closeTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const reportChecklistState = useCallback((cardKey: string, allOwnCommitted: boolean, hasOpenDelegated: boolean) => {
    setDelegatedOpenCardKeys((current) => {
      if (current.has(cardKey) === hasOpenDelegated) return current;
      const next = new Set(current);
      if (hasOpenDelegated) next.add(cardKey);
      else next.delete(cardKey);
      return next;
    });

    if (!allOwnCommitted) {
      const timer = appearTimers.current.get(cardKey);
      if (timer) {
        clearTimeout(timer);
        appearTimers.current.delete(cardKey);
      }
      return;
    }
    if (appearTimers.current.has(cardKey) || closeTimers.current.has(cardKey)) return;
    appearTimers.current.set(
      cardKey,
      setTimeout(() => {
        appearTimers.current.delete(cardKey);
        setConfirmingCardKeys((current) => new Set(current).add(cardKey));
        closeTimers.current.set(
          cardKey,
          setTimeout(() => {
            closeTimers.current.delete(cardKey);
            setConfirmingCardKeys((current) => {
              const next = new Set(current);
              next.delete(cardKey);
              return next;
            });
            setDismissedCardKeys((current) => new Set(current).add(cardKey));
          }, DONE_CLOSE_MS),
        );
      }, DONE_APPEAR_MS),
    );
  }, []);

  useEffect(
    () => () => {
      appearTimers.current.forEach(clearTimeout);
      closeTimers.current.forEach(clearTimeout);
    },
    [],
  );

  const todosById = useMemo(() => new Map(data?.map((todo) => [todo.id, todo] as const) ?? []), [data]);
  const openIdsAtLoad = frozen?.key === idsKey ? frozen.openIds : EMPTY_STATE.openIdsAtLoad;
  // A failed query never resolves the frozen set
  const stillLoading = ids.length > 0 && !error && (isPending || frozen?.key !== idsKey);

  return useMemo(
    () => ({
      todosById,
      openIdsAtLoad,
      isPending: stillLoading,
      confirmingCardKeys,
      dismissedCardKeys,
      delegatedOpenCardKeys,
      reportChecklistState,
    }),
    [
      todosById,
      openIdsAtLoad,
      stillLoading,
      confirmingCardKeys,
      dismissedCardKeys,
      delegatedOpenCardKeys,
      reportChecklistState,
    ],
  );
}
